// PORT-AS-IS from v3 `composables/useCreditGuard.ts` — the body already matches contract §9.
//
// ⚠ This gate has NEVER run in production. It was dead in v3: only its own test imported it, while
// Orpheus and Creative AI each hand-rolled the depletion path (`useOrpheusMessages.js:3-4`,
// `CreativeAiNewAnalysisModal.vue:120-121`). This port promotes it and those copies collapse into
// it, so the FIRST consumer cutover is a real integration test, not a formality (analysis §7.2).
import {
  creditDepletedDetails,
  isCreditDepletedError,
  isPackageForbiddenError,
} from '../service/creditError'
import { useCreditStore } from '../stores/creditStore'

export interface CreditGuard {
  /** True when the error was an out-of-credits 402 — the store is hydrated from its details. */
  handleDepletion: (error: unknown) => boolean
  isCreditDepletedError: typeof isCreditDepletedError
  isPackageForbiddenError: typeof isPackageForbiddenError
  openUpgrade: () => void
  refreshBalance: () => Promise<void>
}

/**
 * Shared handling for billable AI actions (Orpheus send, Creative run, Causal run).
 *
 * Wrap the action's error path with `handleDepletion`: on a 402 CREDIT_DEPLETED it hydrates the
 * credit store straight from the error's embedded `BalancePayload` — so the topbar slot and any
 * locked card reflect server truth with no extra call — and returns `true`. Callers then render
 * their locked state and must NOT also show a generic error toast.
 *
 * Call the underlying request so the 402 reaches you instead of the global handler.
 */
export function useCreditGuard(): CreditGuard {
  const creditStore = useCreditStore()

  function handleDepletion(error: unknown): boolean {
    if (!isCreditDepletedError(error)) return false
    const details = creditDepletedDetails(error)
    if (details) creditStore.setBalance(details)
    return true
  }

  return {
    handleDepletion,
    isCreditDepletedError,
    isPackageForbiddenError,
    openUpgrade: () => creditStore.openUpgrade(),
    refreshBalance: () => creditStore.refresh(),
  }
}
